import { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Sparkles, ArrowLeft, Mail, Lock, Send, Check, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import client from '../api/client'

export default function ForgotPassword() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()

  // Если есть токен — показываем форму нового пароля
  if (token) {
    return <ResetPasswordForm token={token} />
  }

  return <ForgotPasswordForm />
}

function ForgotPasswordForm() {
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      toast.error('Введите email')
      return
    }
    setSending(true)
    try {
      const { data } = await client.post('/auth/forgot-password', { email })
      setSent(true)
      toast.success(data.message || 'Письмо отправлено')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка отправки')
    } finally {
      setSending(false)
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f0f1a]">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial from-indigo-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="w-full max-w-md relative">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold gradient-text">Chat-Neuro.ru</span>
            </Link>
          </div>
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-500/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-indigo-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Проверьте почту</h1>
            <p className="text-sm text-gray-400 mb-2">
              Если аккаунт с email <strong className="text-gray-200">{email}</strong> существует,
              мы отправили на него инструкции по сбросу пароля.
            </p>
            <p className="text-xs text-gray-500 mb-6">Проверьте папку «Спам», если письмо не пришло</p>
            <Link to="/login" className="gradient-btn px-6 py-3 rounded-xl text-sm inline-block">
              Вернуться ко входу
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f0f1a]">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial from-indigo-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">Chat-Neuro.ru</span>
          </Link>
        </div>

        <div className="glass-card p-8">
          <h1 className="text-2xl font-bold text-white mb-1">Восстановление пароля</h1>
          <p className="text-sm text-gray-500 mb-6">
            Введите email, который вы использовали при регистрации
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input-field"
                required
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="gradient-btn w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Отправка...' : 'Отправить инструкции'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              <Link to="/login" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                ← Вернуться ко входу
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

function ResetPasswordForm({ token }: { token: string }) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 6) {
      toast.error('Пароль должен быть минимум 6 символов')
      return
    }
    if (password !== confirmPassword) {
      toast.error('Пароли не совпадают')
      return
    }
    setSaving(true)
    try {
      await client.post('/auth/reset-password', {
        email: token,
        password,
        name: '',
      })
      setDone(true)
      toast.success('Пароль изменён!')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Ошибка сброса пароля')
    } finally {
      setSaving(false)
    }
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f0f1a]">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial from-indigo-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="w-full max-w-md relative">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold gradient-text">Chat-Neuro.ru</span>
            </Link>
          </div>
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Пароль изменён!</h1>
            <p className="text-sm text-gray-400 mb-6">Теперь вы можете войти с новым паролем</p>
            <button
              onClick={() => navigate('/login')}
              className="gradient-btn px-6 py-3 rounded-xl text-sm"
            >
              Войти
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#0f0f1a]">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial from-indigo-500/5 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="w-full max-w-md relative">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold gradient-text">Chat-Neuro.ru</span>
          </Link>
        </div>

        <div className="glass-card p-8">
          <h1 className="text-2xl font-bold text-white mb-1">Новый пароль</h1>
          <p className="text-sm text-gray-500 mb-6">Придумайте новый пароль для вашего аккаунта</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Новый пароль</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Минимум 6 символов"
                  className="input-field pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <Lock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Подтвердите пароль</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Повторите пароль"
                className="input-field"
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className="gradient-btn w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            >
              <Lock className="w-4 h-4" />
              {saving ? 'Сохранение...' : 'Сохранить пароль'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
